// The Trophy Cabinet's badge engine — 12 launch badges spanning the four Learn & Earn games,
// habit streaks, saving, and goals. Earned badges are STORED on the profile ({ id, earnedAt })
// rather than purely derived, so pruned history can never un-earn one; the rules below only
// ever decide whether a not-yet-earned badge unlocks now.
//
// Import graph note: badges → utils → Common → oinks. utils never imports badges, so this is
// acyclic. nowIso comes from constants (also acyclic).

import { nowIso } from "../lib/constants.js";
import { dateStrAddDays, isHabitDayComplete } from "../lib/utils.js";
import { ensureCosmetics } from "../lib/oinks.js";

export const BADGE_OINK_BONUS = 10;

// hint: always shown under a locked badge — kids this age respond to visible targets
// (scope decision R2-3), so no mystery "???" states.
export const BADGES = [
  // `icon` is a sprite id (LB-162, Sep 2026): badges render as the icon on a gold disc when
  // earned and a grey disc when locked. The four with no exact glyph in the set use
  // medal/trophy/star; commission proper marks only if the discs read badly on staging.
  { id: "perfect_spelling", label: "Spelling Star", icon: "ic-spelling", hint: "Get full marks on a spelling test" },
  { id: "perfect_vocab", label: "Word Wizard", icon: "ic-vocab", hint: "Get 100% on a vocab test" },
  { id: "tt_full_run", label: "Streak Machine", icon: "ic-flame", hint: "Max out a TT Streaks run" },
  { id: "maths_100", label: "Number Ninja", icon: "ic-maths", hint: "Get 100% on Maths Workout" },
  { id: "streak_7", label: "Week of Fire", icon: "ic-medal", hint: "A 7-day daily-task streak" },
  { id: "streak_30", label: "Month of Fire", icon: "ic-trophy", hint: "A 30-day daily-task streak" },
  { id: "q_100", label: "Century", icon: "ic-star", hint: "Answer 100 questions" },
  { id: "q_500", label: "Big Brain", icon: "ic-brain", hint: "Answer 500 questions" },
  { id: "first_vault", label: "Super Saver", icon: "ic-supersaver", hint: "Make your first Savings Vault transfer" },
  { id: "first_goal", label: "Goal Getter", icon: "ic-goals", hint: "Redeem your first goal" },
  { id: "all_four_week", label: "All-Rounder", icon: "ic-medal", hint: "Play all 4 games in one week" },
  { id: "words_25", label: "Word Collector", icon: "ic-vocab", hint: "Master 25 vocab words" },
];

export function findBadge(id) {
  return BADGES.find((b) => b.id === id) || null;
}

export function earnedBadgeIds(profile) {
  return new Set(((profile && profile.badges) || []).map((b) => b.id));
}

// Best-EVER consecutive streak, not the current one (scope decision R2-4): a broken streak
// wiping 25 days of progress toward Month of Fire is exactly the kind of thing that makes a
// nine-year-old quit — the badge remembers the child's best run. One pass over the child's
// distinct logged dates, checking day-completeness only on candidates (cheap: habitLog is
// small and this runs on session/habit events, not per render).
export function bestEverHabitStreak(habitLog, habits, childId) {
  const dates = [...new Set((habitLog || []).filter((l) => l.childId === childId).map((l) => l.date))].sort();
  let best = 0;
  let run = 0;
  let prev = null;
  for (const d of dates) {
    if (!isHabitDayComplete(habitLog, habits, childId, d)) { run = 0; prev = null; continue; }
    run = (prev !== null && dateStrAddDays(prev, 1) === d) ? run + 1 : 1;
    prev = d;
    if (run > best) best = run;
  }
  return best;
}

// Evaluate every not-yet-earned badge for this child against current state. Mutates `state`
// (push onto profile.badges, plus the +10 OP bonus per badge when Oinks are on for the child)
// and returns the array of newly earned badge ids so the caller can toast/celebrate. Safe to
// call repeatedly — already-earned badges are never re-evaluated or re-awarded.
export function checkAndAwardBadges(state, childId) {
  const p = (state.profiles || []).find((x) => x.id === childId);
  if (!p) return [];
  if (!Array.isArray(p.badges)) p.badges = [];
  const lc = (state.learningContent || {})[childId] || {};
  const st = lc.stats || {};
  const perfect = st.perfect || {};
  const earned = earnedBadgeIds(p);

  const masteredWords = ((lc.vocabBank || []).filter((v) => (v.correctCount || 0) >= 2)).length;
  const bestStreak = bestEverHabitStreak(state.habitLog || [], state.habits || [], childId);

  const rules = {
    perfect_spelling: () => (perfect.spelling || 0) >= 1,
    perfect_vocab: () => (perfect.vocab || 0) >= 1,
    tt_full_run: () => (perfect.ttstreaks || 0) >= 1,
    maths_100: () => (perfect.mathsworkout || 0) >= 1,
    streak_7: () => bestStreak >= 7,
    streak_30: () => bestStreak >= 30,
    q_100: () => (st.questionsAnswered || 0) >= 100,
    q_500: () => (st.questionsAnswered || 0) >= 500,
    first_vault: () => (state.transactions || []).some((t) => t.childId === childId && t.vaultTransfer),
    first_goal: () => (state.requests || []).some((r) => r.childId === childId && r.requestType === "goal_redeem" && r.status === "approved"),
    all_four_week: () => Array.isArray(st.weekKinds && st.weekKinds.kinds) && st.weekKinds.kinds.length >= 4,
    words_25: () => masteredWords >= 25,
  };

  const oinkBonus = lc.oinksEnabled !== false;
  const newly = [];
  for (const badge of BADGES) {
    if (earned.has(badge.id)) continue;
    let hit = false;
    try { hit = rules[badge.id] ? rules[badge.id]() : false; } catch { hit = false; }
    if (!hit) continue;
    p.badges.push({ id: badge.id, earnedAt: nowIso() });
    if (oinkBonus) { ensureCosmetics(p); p.oinks += BADGE_OINK_BONUS; }
    newly.push(badge.id);
  }
  return newly;
}
